'use client';

import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Program {
  id: string;
  name: string;
  badge: string;
  headline: string;
  minAmount: string;
  maxAmount: string;
  termRange: string;
  keyBenefit: string;
  tags: string[];
  span?: 'wide' | 'normal';
}

const PROGRAMS: Program[] = [
  {
    id: 'sba',
    name: 'SBA Loans',
    badge: 'Small Business Administration',
    headline: 'Up to $5M. Government-backed. Best rates.',
    minAmount: '$50K',
    maxAmount: '$5M',
    termRange: '10–25 yrs',
    keyBenefit: 'The gold standard for small business financing. Lower rates, longer terms, and higher approval rates than conventional business loans.',
    tags: ['Small Business', 'Low Rate', 'Long Term'],
    span: 'wide',
  },
  {
    id: 'term-loan',
    name: 'Term Loans',
    badge: 'Business Financing',
    headline: 'Fixed payments. Predictable cash flow.',
    minAmount: '$10K',
    maxAmount: '$2M',
    termRange: '1–7 yrs',
    keyBenefit: 'Ideal for one-time investments — equipment, renovations, or expansion. Fixed monthly payments make budgeting straightforward.',
    tags: ['Fixed Rate', 'Fast Funding', 'All Industries'],
  },
  {
    id: 'loc',
    name: 'Line of Credit',
    badge: 'Revolving Credit',
    headline: 'Draw when you need it. Pay only what you use.',
    minAmount: '$10K',
    maxAmount: '$500K',
    termRange: 'Revolving',
    keyBenefit: 'Flexible access to capital for ongoing needs. Only pay interest on what you draw — perfect for managing cash flow gaps.',
    tags: ['Flexible', 'Revolving', 'Working Capital'],
  },
  {
    id: 'personal',
    name: 'Personal Loans',
    badge: 'Consumer Lending',
    headline: 'Debt consolidation, major purchases & more.',
    minAmount: '$5K',
    maxAmount: '$100K',
    termRange: '1–7 yrs',
    keyBenefit: 'Unsecured personal loans for any purpose — consolidate high-interest debt, fund a major purchase, or cover unexpected expenses.',
    tags: ['Unsecured', 'Fast Approval', 'Any Purpose'],
    span: 'wide',
  },
];

export default function ProgramsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    document.getElementById('qualify-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="programs" className="py-24 lg:py-32 bg-cloud overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 reveal">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-light border border-sky/20 text-xs font-semibold text-sky-dark font-body uppercase tracking-wide mb-5">
              Loan Programs
            </span>
            <h2 className="font-heading font-bold text-display text-slate">
              The right loan exists.<br />
              <span className="text-sky">We'll help you find it.</span>
            </h2>
          </div>
          <p className="font-body text-slate-fog text-base max-w-sm lg:text-right">
            We match your financial profile against 200+ lending programs to find the best fit.
          </p>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-min">
          {PROGRAMS.map((prog, idx) => {
            const isWide = prog.span === 'wide';
            const accentColors: Record<string, { bg: string; text: string; border: string; tag: string }> = {
              sba: { bg: 'bg-sky-light', text: 'text-sky-dark', border: 'border-sky/30', tag: 'bg-sky/10 text-sky-dark' },
              'term-loan': { bg: 'bg-sage-light', text: 'text-sage-dark', border: 'border-sage/30', tag: 'bg-sage/10 text-sage-dark' },
              loc: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', tag: 'bg-amber-100 text-amber-700' },
              personal: { bg: 'bg-sky-pale', text: 'text-slate', border: 'border-slate-mist/60', tag: 'bg-cloud text-slate-light' },
            };
            const colors = accentColors[prog.id];

            return (
              <div
                key={prog.id}
                className={`program-card bg-white rounded-4xl p-7 lg:p-8 border border-slate-mist/40 shadow-card flex flex-col justify-between reveal ${
                  isWide ? 'md:col-span-2' : ''
                }`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {/* Badge */}
                <div className="mb-6">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold font-body uppercase tracking-wide ${colors.bg} ${colors.text} border ${colors.border} mb-4`}
                  >
                    {prog.badge}
                  </span>
                  <h3 className="font-heading font-bold text-xl lg:text-2xl text-slate mb-2 tracking-tight">
                    {prog.name}
                  </h3>
                  <p className={`font-body font-semibold text-base ${colors.text}`}>
                    {prog.headline}
                  </p>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: 'Min. Amount', value: prog.minAmount },
                    { label: 'Max. Amount', value: prog.maxAmount },
                    { label: 'Term Range', value: prog.termRange },
                  ].map((s) => (
                    <div key={s.label} className="bg-cloud rounded-2xl p-3 text-center">
                      <p className="font-heading font-bold text-slate text-sm lg:text-base">{s.value}</p>
                      <p className="font-body text-xs text-slate-fog mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Key benefit */}
                <p className="font-body text-sm text-slate-light leading-relaxed mb-5">
                  {prog.keyBenefit}
                </p>

                {/* Tags + CTA */}
                <div className="flex items-end justify-between gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {prog.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`text-xs px-2.5 py-1 rounded-full font-body font-medium ${colors.tag}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={scrollToForm}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-sky hover:text-sky-dark font-body transition-colors group"
                  >
                    Check eligibility
                    <Icon name="ArrowRightIcon" size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div className="mt-10 text-center reveal">
          <p className="font-body text-sm text-slate-fog">
            Not sure which loan fits your needs?{' '}
            <button onClick={scrollToForm} className="text-sky font-semibold hover:underline">
              Our form matches you in 90 seconds.
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}